var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import * as React from 'react';
import { Alert, Box, Dialog, DialogActions, DialogContent, Divider, Grid, Typography, } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { APPLICATION_TYPE_OPTIONS } from '../../../config/constants';
import { FORM_FIELD_TOKENS } from '../../FormComponents/dynamicForm';
import CustomButton from '../../buttonComponent/CustomButton';
import ApplicationTypeTile from './ApplicationTypeTile';
import LookupSelect from './LookupSelect';
import { fetchDealerships, fetchLocations, fetchProgramCatalogue, fetchSalesReps, fetchVendorConfigDetails, getFieldConfigForApplicationType, } from './application_lookup_service';
import { ADD_ANOTHER_DEALER_BUTTON_SX, DEALER_ENTRY_SX, DIALOG_ACTIONS_SX, DIALOG_CONTENT_SX, DIALOG_PAPER_SX, DIALOG_TITLE_SX, LOOKUP_ERROR_SX, REMOVE_DEALER_ENTRY_BUTTON_SX, SECTION_DIVIDER_SX, SECTION_LABEL_SX, } from './start_application_styles';
import { useLookupCascade } from './use_lookup_cascade';
var dealerEntrySeq = 0;
var createDealerEntry = function () {
    dealerEntrySeq += 1;
    return {
        id: "dealer-".concat(dealerEntrySeq),
        dealer: '',
        location: '',
        salesRep: '',
        locationOptions: [],
        salesRepOptions: [],
        isLoadingLocations: false,
        isLoadingSalesReps: false,
    };
};
var StartApplicationDialog = function (_a) {
    var _b;
    var open = _a.open, onClose = _a.onClose, onStart = _a.onStart;
    var _c = React.useState('type'), step = _c[0], setStep = _c[1];
    var _d = React.useState(''), applicationType = _d[0], setApplicationType = _d[1];
    var _e = React.useState(function () { return [createDealerEntry()]; }), dealerEntries = _e[0], setDealerEntries = _e[1];
    var _f = React.useState([]), dealerOptions = _f[0], setDealerOptions = _f[1];
    var _g = React.useState(false), isLoadingDealers = _g[0], setIsLoadingDealers = _g[1];
    var _h = React.useState(null), vendorConfigDetails = _h[0], setVendorConfigDetails = _h[1];
    var rowRequestIdsRef = React.useRef({});
    var _j = useLookupCascade(), values = _j.values, options = _j.options, loadingLevel = _j.loadingLevel, error = _j.error, reset = _j.reset, select = _j.select, load = _j.load, getSelectedOption = _j.getSelectedOption, getSelectedLabel = _j.getSelectedLabel;
    React.useEffect(function () {
        if (!open) {
            return;
        }
        setStep('type');
        setApplicationType('');
        setDealerEntries([createDealerEntry()]);
        setDealerOptions([]);
        setVendorConfigDetails(null);
        rowRequestIdsRef.current = {};
        reset();
        load('program', fetchProgramCatalogue);
    }, [load, open, reset]);
    var handleProgramChange = React.useCallback(function (programId) { return select('program', programId); }, [select]);
    React.useEffect(function () {
        if (!values.program) {
            setDealerOptions([]);
            setDealerEntries([createDealerEntry()]);
            setVendorConfigDetails(null);
            return;
        }
        var isCancelled = false;
        setIsLoadingDealers(true);
        setDealerEntries([createDealerEntry()]);
        setVendorConfigDetails(null);
        fetchDealerships(values.program).then(function (result) {
            if (isCancelled) {
                return;
            }
            setDealerOptions(result.options);
            setIsLoadingDealers(false);
            fetchVendorConfigDetails(result.vendorId).then(function (details) {
                if (!isCancelled) {
                    setVendorConfigDetails(details);
                }
            });
        });
        return function () {
            isCancelled = true;
        };
    }, [values.program]);
    var handleNext = React.useCallback(function () {
        if (!applicationType) {
            return;
        }
        setStep('details');
    }, [applicationType]);
    var handleBack = React.useCallback(function () {
        setStep('type');
    }, []);
    var handleAddDealerEntry = React.useCallback(function () {
        setDealerEntries(function (prev) { return __spreadArray(__spreadArray([], prev, true), [createDealerEntry()], false); });
    }, []);
    var handleRemoveDealerEntry = React.useCallback(function (id) {
        setDealerEntries(function (prev) { return (prev.length > 1 ? prev.filter(function (entry) { return entry.id !== id; }) : prev); });
    }, []);
    var loadRowLocations = React.useCallback(function (id, dealerAspireOId) {
        var _a;
        var requestId = ((_a = rowRequestIdsRef.current[id]) !== null && _a !== void 0 ? _a : 0) + 1;
        rowRequestIdsRef.current[id] = requestId;
        if (!dealerAspireOId) {
            setDealerEntries(function (prev) {
                return prev.map(function (entry) {
                    return entry.id === id ? __assign(__assign({}, entry), { locationOptions: [], isLoadingLocations: false }) : entry;
                });
            });
            return;
        }
        setDealerEntries(function (prev) {
            return prev.map(function (entry) { return (entry.id === id ? __assign(__assign({}, entry), { isLoadingLocations: true }) : entry); });
        });
        fetchLocations(dealerAspireOId).then(function (result) {
            if (rowRequestIdsRef.current[id] !== requestId) {
                return;
            }
            setDealerEntries(function (prev) {
                return prev.map(function (entry) {
                    return entry.id === id
                        ? __assign(__assign({}, entry), { locationOptions: result.options, isLoadingLocations: false }) : entry;
                });
            });
        });
    }, []);
    var loadRowSalesReps = React.useCallback(function (id, dealerAspireOId, location) {
        var _a;
        var requestId = ((_a = rowRequestIdsRef.current[id]) !== null && _a !== void 0 ? _a : 0) + 1;
        rowRequestIdsRef.current[id] = requestId;
        if (!dealerAspireOId || !location) {
            setDealerEntries(function (prev) {
                return prev.map(function (entry) {
                    return entry.id === id
                        ? __assign(__assign({}, entry), { salesRepOptions: [], isLoadingSalesReps: false }) : entry;
                });
            });
            return;
        }
        setDealerEntries(function (prev) {
            return prev.map(function (entry) { return (entry.id === id ? __assign(__assign({}, entry), { isLoadingSalesReps: true }) : entry); });
        });
        fetchSalesReps(dealerAspireOId, location.locationAspireOId, location.locationCode).then(function (result) {
            if (rowRequestIdsRef.current[id] !== requestId) {
                return;
            }
            setDealerEntries(function (prev) {
                return prev.map(function (entry) {
                    return entry.id === id
                        ? __assign(__assign({}, entry), { salesRepOptions: result.options, isLoadingSalesReps: false }) : entry;
                });
            });
        });
    }, []);
    var handleDealerEntryFieldChange = React.useCallback(function (id, field, value) {
        var _a;
        var clearedFields = field === 'dealer'
            ? { location: '', salesRep: '', locationOptions: [], salesRepOptions: [] }
            : field === 'location'
                ? { salesRep: '', salesRepOptions: [] }
                : {};
        setDealerEntries(function (prev) {
            return prev.map(function (entry) {
                var _a;
                return entry.id === id ? __assign(__assign(__assign({}, entry), clearedFields), (_a = {}, _a[field] = value, _a)) : entry;
            });
        });
        if (field === 'dealer') {
            loadRowLocations(id, value);
        }
        else if (field === 'location') {
            var currentEntry = dealerEntries.find(function (entry) { return entry.id === id; });
            loadRowSalesReps(id, (_a = currentEntry === null || currentEntry === void 0 ? void 0 : currentEntry.dealer) !== null && _a !== void 0 ? _a : '', currentEntry === null || currentEntry === void 0 ? void 0 : currentEntry.locationOptions.find(function (option) { return option.value === value; }));
        }
    }, [dealerEntries, loadRowLocations, loadRowSalesReps]);
    var handleStart = React.useCallback(function () {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        var selectedType = APPLICATION_TYPE_OPTIONS.find(function (option) { return option.value === applicationType; });
        if (!selectedType || !values.program) {
            return;
        }
        var selectedProgram = getSelectedOption('program', values.program);
        var resolvedDealerEntries = dealerEntries
            .filter(function (entry) { return entry.dealer; })
            .map(function (entry) {
            var _a, _b, _c, _d, _e, _f;
            var dealerOption = dealerOptions.find(function (option) { return option.value === entry.dealer; });
            var locationOption = entry.locationOptions.find(function (option) { return option.value === entry.location; });
            var salesRepOption = entry.salesRepOptions.find(function (option) { return option.value === entry.salesRep; });
            return {
                dealershipId: entry.dealer,
                dealer: (_a = dealerOption === null || dealerOption === void 0 ? void 0 : dealerOption.label) !== null && _a !== void 0 ? _a : '',
                locationId: (_b = locationOption === null || locationOption === void 0 ? void 0 : locationOption.locationAspireOId) !== null && _b !== void 0 ? _b : '',
                locationCode: (_c = locationOption === null || locationOption === void 0 ? void 0 : locationOption.locationCode) !== null && _c !== void 0 ? _c : '',
                location: (_d = locationOption === null || locationOption === void 0 ? void 0 : locationOption.label) !== null && _d !== void 0 ? _d : '',
                salesRepId: entry.salesRep,
                salesRep: (_e = salesRepOption === null || salesRepOption === void 0 ? void 0 : salesRepOption.label) !== null && _e !== void 0 ? _e : '',
                salesRepAccountAspireId: (_f = salesRepOption === null || salesRepOption === void 0 ? void 0 : salesRepOption.accountAspireId) !== null && _f !== void 0 ? _f : '',
            };
        });
        var primaryDealerEntry = resolvedDealerEntries[0];
        onStart({
            applicationType: applicationType,
            applicationTypeLabel: selectedType.label,
            wizardTitle: selectedType.wizardTitle,
            programId: values.program,
            programAspireOId: (_a = selectedProgram === null || selectedProgram === void 0 ? void 0 : selectedProgram.programAspireOId) !== null && _a !== void 0 ? _a : '',
            programCountry: (_b = selectedProgram === null || selectedProgram === void 0 ? void 0 : selectedProgram.country) !== null && _b !== void 0 ? _b : '',
            program: getSelectedLabel('program'),
            portalId: '',
            portal: '',
            dealershipId: (_c = primaryDealerEntry === null || primaryDealerEntry === void 0 ? void 0 : primaryDealerEntry.dealershipId) !== null && _c !== void 0 ? _c : '',
            dealer: (_d = primaryDealerEntry === null || primaryDealerEntry === void 0 ? void 0 : primaryDealerEntry.dealer) !== null && _d !== void 0 ? _d : '',
            locationId: (_e = primaryDealerEntry === null || primaryDealerEntry === void 0 ? void 0 : primaryDealerEntry.locationId) !== null && _e !== void 0 ? _e : '',
            locationCode: (_f = primaryDealerEntry === null || primaryDealerEntry === void 0 ? void 0 : primaryDealerEntry.locationCode) !== null && _f !== void 0 ? _f : '',
            location: (_g = primaryDealerEntry === null || primaryDealerEntry === void 0 ? void 0 : primaryDealerEntry.location) !== null && _g !== void 0 ? _g : '',
            salesRepId: (_h = primaryDealerEntry === null || primaryDealerEntry === void 0 ? void 0 : primaryDealerEntry.salesRepId) !== null && _h !== void 0 ? _h : '',
            salesRep: (_j = primaryDealerEntry === null || primaryDealerEntry === void 0 ? void 0 : primaryDealerEntry.salesRep) !== null && _j !== void 0 ? _j : '',
            dealerEntries: resolvedDealerEntries,
            fieldConfig: getFieldConfigForApplicationType(null, applicationType),
            vendorConfig: vendorConfigDetails,
            fieldConfigBundle: null,
        });
    }, [
        applicationType,
        dealerEntries,
        dealerOptions,
        getSelectedLabel,
        getSelectedOption,
        onStart,
        values.program,
        vendorConfigDetails,
    ]);
    var isTypeStep = step === 'type';
    return (React.createElement(Dialog, { open: open, onClose: onClose, fullWidth: true, maxWidth: "sm", "aria-labelledby": "start-application-title", PaperProps: { sx: DIALOG_PAPER_SX } },
        React.createElement(DialogContent, { sx: DIALOG_CONTENT_SX },
            React.createElement(Typography, { id: "start-application-title", component: "h2", sx: DIALOG_TITLE_SX }, isTypeStep
                ? 'Start Your Application By providing Below Details'
                : 'Application Details'),
            isTypeStep ? (React.createElement(React.Fragment, null,
                React.createElement(Typography, { sx: SECTION_LABEL_SX }, "Select Your Application Type"),
                React.createElement(Divider, { sx: SECTION_DIVIDER_SX }),
                React.createElement(Box, { role: "radiogroup", "aria-label": "Application type" }, APPLICATION_TYPE_OPTIONS.map(function (option) { return (React.createElement(ApplicationTypeTile, { key: option.value, value: option.value, label: option.label, description: option.description, isSelected: applicationType === option.value, onSelect: setApplicationType })); })))) : (React.createElement(React.Fragment, null,
                React.createElement(Typography, { sx: SECTION_LABEL_SX }, "Provide the partner and program details for this application"),
                React.createElement(Divider, { sx: SECTION_DIVIDER_SX }),
                error && (React.createElement(Alert, { severity: "error", sx: LOOKUP_ERROR_SX }, error)),
                React.createElement(Grid, { container: true, spacing: 2 },
                    React.createElement(Grid, { item: true, xs: 12 },
                        React.createElement(LookupSelect, { id: "startApplicationProgram", label: "Program", value: values.program, options: options.program, isLoading: loadingLevel === 'program', isDisabled: false, isSearchable: true, onChange: handleProgramChange }))),
                dealerEntries.map(function (entry, index) { return (React.createElement(Box, { key: entry.id, sx: index === 0 ? __assign(__assign({}, DEALER_ENTRY_SX), { mt: 2.5 }) : DEALER_ENTRY_SX },
                    React.createElement(Grid, { container: true, spacing: 2 },
                        React.createElement(Grid, { item: true, xs: 12 },
                            React.createElement(LookupSelect, { id: "startApplicationDealer-".concat(entry.id), label: "Dealer", value: entry.dealer, options: dealerOptions.filter(function (option) {
                                    return option.value === entry.dealer ||
                                        !dealerEntries.some(function (otherEntry) {
                                            return otherEntry.id !== entry.id &&
                                                otherEntry.dealer === option.value;
                                        });
                                }), isLoading: isLoadingDealers, isDisabled: !values.program, isSearchable: true, onChange: function (value) {
                                    return handleDealerEntryFieldChange(entry.id, 'dealer', value);
                                } })),
                        React.createElement(Grid, { item: true, xs: 12 },
                            React.createElement(LookupSelect, { id: "startApplicationLocation-".concat(entry.id), label: "Location", value: entry.location, options: entry.locationOptions, isLoading: entry.isLoadingLocations, isDisabled: !entry.dealer, isSearchable: true, isOptional: index > 0, onChange: function (value) {
                                    return handleDealerEntryFieldChange(entry.id, 'location', value);
                                } })),
                        React.createElement(Grid, { item: true, xs: 12 },
                            React.createElement(LookupSelect, { id: "startApplicationSalesRep-".concat(entry.id), label: "Sales Rep", value: entry.salesRep, options: entry.salesRepOptions, isLoading: entry.isLoadingSalesReps, isDisabled: !entry.location, isOptional: true, onChange: function (value) {
                                    return handleDealerEntryFieldChange(entry.id, 'salesRep', value);
                                } }))),
                    index > 0 && (React.createElement(CustomButton, { variant: "text", MDButton: true, fullWidth: false, startIcon: React.createElement(DeleteOutlineIcon, { fontSize: "small" }), onClick: function () { return handleRemoveDealerEntry(entry.id); }, sx: REMOVE_DEALER_ENTRY_BUTTON_SX }, "Remove Dealer")))); }),
                React.createElement(CustomButton, { variant: "text", MDButton: true, fullWidth: false, startIcon: React.createElement(AddCircleOutlineIcon, { fontSize: "small" }), onClick: handleAddDealerEntry, sx: ADD_ANOTHER_DEALER_BUTTON_SX }, "Add Another Dealer")))),
        React.createElement(DialogActions, { sx: DIALOG_ACTIONS_SX }, isTypeStep ? (React.createElement(React.Fragment, null,
            React.createElement(CustomButton, { variant: "secondary", MDButton: true, fullWidth: false, onClick: onClose }, "Cancel"),
            React.createElement(CustomButton, { variant: "contained", MDButton: true, fullWidth: false, bgColor: FORM_FIELD_TOKENS.actionColor, disabled: !applicationType, onClick: handleNext }, "Next"))) : (React.createElement(React.Fragment, null,
            React.createElement(CustomButton, { variant: "secondary", MDButton: true, fullWidth: false, onClick: handleBack }, "Back"),
            React.createElement(CustomButton, { variant: "contained", MDButton: true, fullWidth: false, bgColor: FORM_FIELD_TOKENS.actionColor, disabled: !values.program || !((_b = dealerEntries[0]) === null || _b === void 0 ? void 0 : _b.location), onClick: handleStart }, "Continue"))))));
};
export default StartApplicationDialog;
